import { useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import router from 'next/router';
import { Post } from 'lib/types';
import { User } from 'lib/types/users';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { PostSearchCriteria } from 'lib/types/filters';
import { FilterCriteria } from 'components/UserSummary/FilterCriteria';
import { SimpleCard } from 'components/SimpleCard';
import { notify } from 'lib/utils/honeybadgerUtils';

interface Props {
  selectedPosts: Post[];
  onChange: (posts: Post[]) => void;
  onCancel: () => void;
  limit?: number;
}

export const PostSearch = ({
  selectedPosts, onChange, onCancel, limit,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();

  const [posts, setPosts] = useState<Post[]>([]);
  const [collaborators, setCollaborators] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const count = selectedPosts.length;

  const change = (checked: boolean, post: Post) => {
    if (checked) {
      if (!limit || (limit && count < limit)) {
        onChange(selectedPosts.concat(post));
      }
      return;
    }
    onChange(selectedPosts.filter((p) => p.id !== post.id));
  };

  const fetchPosts = async (criteria: PostSearchCriteria) => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const [{ data }, collab] = await Promise.all(
        [API.getProfilePosts(currentUser.id, criteria), API.getCollaborators(currentUser?.id)],
      );
      setPosts(data);
      setCollaborators(collab);
      setIsLoading(false);
    } catch (err: any) {
      if (err.name === 'AbortError' || err === 'CANCELLED') {
        return;
      }
      setError(err);
      notify(err, 'WorkForUs/PostSearch/fetchPosts', {
        queryParams: router.query,
      });
      setIsLoading(false);
    }
  };

  if (!currentUser) return <div>Please sign in to continue</div>;

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Something went wrong fetching data. Please double check your filters!
      </Alert>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-lg md:text-xl font-bold mb-4">Select up to 3 posts you want to showcase</h1>
      <div className="mb-4">
        <FilterCriteria
          onChange={fetchPosts}
          profileUser={{
            ...currentUser,
            collaborators,
          }}
        />
      </div>
      <div className="font-semibold mb-2">
        {count}
        {' '}
        Post(s) Selected
      </div>
      <div className="h-[53vh] overflow-auto p-2 md:p-0 mb-4">
        {isLoading && <div className="w-full text-center py-4">Loading...</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          {posts.map((p) => (
            <SimpleCard
              post={p}
              key={p.id}
              checked={!!selectedPosts.find((s) => s.id === p.id)}
              onChange={(e) => change(e, p)}
            />
          ))}
        </div>
      </div>
      <div className="w-full text-right">
        <button
          type="button"
          className="btn-primary"
          onClick={onCancel}
        >
          Next
        </button>
      </div>
    </div>
  );
};
